import clone from 'clone';
import _ from 'lodash';

export default class DirtyEngine {
	/**
	 * preload with starting data values
	 *
	 * @param(...object) set of starting data in key/value object pairs
	 */
	constructor() {
		this.isDirty = this.isDirty.bind(this);
		this.isDirtyClassName = this.isDirtyClassName.bind(this);

		const datas = Array.prototype.slice.call(arguments);
		this.originalValues = _.reduce(datas, (finalResult, data) =>
			_.reduce(data, (result, value, key) => {
				result[key] = clone(value);
				return result;
			}, finalResult)
		, {});
	}

	/**
	 * check if data is dirty compared with starting values
	 *
	 * @param data what the data looks like now
	 * @param field {undefined|string} the specfic field to check or undefined to check them all
	 * @param isDirtyTrump {undefined|boolean} can trump if dirty is T/F; inputs get this passed to them so allow it to go through the engine
	 * @return {boolean|string[]} true if any field keys are different or values are different
	 */
	isDirty(data, field = undefined, isDirtyTrump = undefined) {
		let isDirty = isDirtyTrump;
		if (isDirty === undefined) {
			if (field) {
				isDirty = this.compareValues(data[field], this.originalValues[field]);
			} else {
				// check that the field lists match between the old and new data
				const originalFields = Object.keys(this.originalValues);
				const newFields = Object.keys(data);
				isDirty = newFields.length !== originalFields.length;
				// if fields match then check that field values match
				if (!isDirty) {
					isDirty = _.some(this.originalValues, (value, key) => this.compareValues(value, data[key]));
				}
			}
		}
		return isDirty;
	}

	/**
	 *
	 * @param a
	 * @param b
	 * @return {boolean} true if isDirty
	 */
	compareValues(a, b) {
		let isDirty;
		if (_.isArray(a) || _.isArray(b)) {
			isDirty = this.compareArrays(_.castArray(a), _.castArray(b));
		} else if (_.isObject(a) && _.isObject(b)) {
			isDirty = this.compareObjects(a, b);
		} else {
			// if only doing one field, check that one field
			isDirty = a !== b;
		}
		return isDirty;
	}

	/**
	 *
	 * @param a
	 * @param b
	 * @return {boolean} true if isDirty
	 */
	compareObjects(a, b) {
		const keysA = Object.keys(a);
		const keysB = Object.keys(b);
		let isDirty = this.compareValues(keysA, keysB);
		if (!isDirty) {
			isDirty = _.some(keysA, key => this.compareValues(a[key], b[key]));
		}
		return isDirty;
	}

	/**
	 *
	 * @param a
	 * @param b
	 * @return {boolean} true if isDirty
	 */
	compareArrays(a, b) {
		let isDirty = a.length !== b.length;
		if (!isDirty) {
			isDirty = _.some(a, (value, idx) => this.compareValues(value, b[idx]));
		}
		return isDirty;
	}

	/**
	 * check if data is dirty compared with starting values and, if so, return a 'dirty' class name
	 *
	 * @param data what the data looks like now
	 * @param field {undefined|string} the specfic field to check or undefined to check them all
	 * @param isDirtyTrump {undefined|boolean} can trump if dirty is T/F; inputs get this passed to them so allow it to go through the engine
	 * @return {string} the classname to put on a DOM element if the field is dirty
	 */
	isDirtyClassName(data, field = undefined, isDirtyTrump = undefined) {
		return this.isDirty(data, field, isDirtyTrump) ? DirtyEngine.DIRTY_CLASSNAME : '';
	}
}

DirtyEngine.DIRTY_CLASSNAME = 'dirty';